import { useState, type ComponentProps } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { CommentEditor } from '@/tiptap/tiptap-extensions/custom/comments/comment-editor/comment-editor'
import { CommentViewer } from '@/tiptap/tiptap-extensions/custom/comments/comment-editor/comment-viewer'
import type { Comment } from '@/tiptap/tiptap-extensions/custom/comments/types'
import { useAtom } from 'jotai'
import {
  MoreHorizontal,
  SquarePen,
  Check,
  X,
  Lightbulb,
  Trash,
  Reply,
  Link,
  Sparkles,
} from 'lucide-react'
import { currentUserAtom } from '@/stores/sessionStore'
import { cn } from '@/lib/utils'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@deepguide-ai/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import {
  getCommentAuthorLabel,
  isIntegrationComment,
} from '@/components/comments/comment-display'

export interface CommentContentProps {
  comment: Comment
  onResolve: (
    commentId: string,
    resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'
  ) => void
  onDelete: (commentId: string) => void
  onReply: (
    parentId: string,
    content: string,
    extra?: { mentions?: string[]; richContent?: any }
  ) => void
  onEditComment?: (commentId: string, content: string, metadata?: any) => void
  onApplySuggestion?: (commentId: string) => void
  onDismissCheck?: (commentId: string) => void
  members?: Array<{ id: string; name?: string | null; email?: string | null }>
  showActions?: boolean
  className?: string
  // Show minimized view (header only, expandable)
  isMinimized?: boolean
  isLocked?: boolean
  allowEdit?: boolean
  allowDelete?: boolean
  allowCopyLink?: boolean
  allowResolve?: boolean
  alwaysShowReplyEditor?: boolean
}

export function CommentContent({
  comment,
  onResolve,
  onDelete,
  onReply,
  onEditComment,
  onApplySuggestion,
  onDismissCheck,
  members = [],
  showActions = true,
  className,
  isMinimized: _isMinimized = false,
  isLocked = false,
  allowEdit = true,
  allowDelete = true,
  allowCopyLink = true,
  allowResolve = true,
  alwaysShowReplyEditor = false,
}: CommentContentProps) {
  const [currentUser] = useAtom(currentUserAtom)
  const [isEditing, setIsEditing] = useState(false)
  const [replyingToId, setReplyingToId] = useState<string | null>(null) // Track which comment/reply we're replying to
  const [isReplyEditing, setIsReplyEditing] = useState(false)
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null)

  const copyPermalink = (commentId: string) => {
    const url = new URL(window.location.href)
    url.searchParams.set('comment', commentId)
    navigator.clipboard.writeText(url.toString())
  }

  const isResolved =
    comment.commentStatus === 'RESOLVED' ||
    comment.commentStatus === 'DISMISSED' ||
    comment.commentStatus === 'IMPLEMENTED'
  const isSyncedComment = isIntegrationComment(comment)

  const getSourceBadge = () => {
    if (comment.source === 'AI') {
      return (
        <Badge variant='secondary' className='gap-1'>
          <Sparkles className='h-3 w-3' />
          AI
        </Badge>
      )
    }
    if (comment.source === 'AUTOMATIC_CHECK') {
      const checkType = comment.metadata?.checkType || 'Check'
      type Severity = 'error' | 'warning' | 'info'
      const severity = (comment.metadata?.severity || 'info') as Severity
      const severityColor: Record<
        Severity,
        'destructive' | 'outline' | 'secondary' | 'default'
      > = {
        error: 'destructive',
        warning: 'outline',
        info: 'secondary',
      }

      return (
        <Badge variant={severityColor[severity]} className='gap-1'>
          {checkType.charAt(0).toUpperCase() + checkType.slice(1)}
        </Badge>
      )
    }
    if (isSyncedComment) {
      return (
        <Badge variant='outline' className='gap-1 text-[10px]'>
          Webhook Sync
        </Badge>
      )
    }
    return null
  }

  const formatTime = (date: Date) => {
    const distance = formatDistanceToNow(date, { addSuffix: false })
    return distance
      .replace('about ', '')
      .replace('almost ', '')
      .replace('over ', '')
      .replace('less than a', '< 1 ')
      .replace(' minutes', 'min')
      .replace(' minute', 'min')
      .replace(' hours', 'h')
      .replace(' hour', 'h')
      .replace(' days', 'd')
      .replace(' day', 'd')
      .replace(' months', 'mon')
      .replace(' month', 'mon')
  }

  const customBadges = (((comment as any)?.metadata?.customBadges ?? []) as Array<{
    label: string
    variant?: ComponentProps<typeof Badge>['variant']
    className?: string
  }>).filter((badge) => Boolean(badge?.label))

  const hasThreadActions =
    allowCopyLink
    || (allowEdit && !!onEditComment && comment.source !== 'AUTOMATIC_CHECK' && !isResolved && !isLocked)
    || (allowDelete && !isResolved && !isLocked)

  return (
    <div className={cn('group', className, { 'opacity-50': isResolved })}>
      {/* Header */}
      <div className='bg-muted flex items-center justify-between gap-1.5 px-3 py-2.5'>
        <div className='flex items-center gap-1.5'>
          {comment.metadata?.suggestion ? (
            <Lightbulb className='h-4 w-4' />
          ) : isSyncedComment ? (
            <span className='bg-muted-foreground/10 text-muted-foreground flex size-5 items-center justify-center rounded-full'>
              <Link className='h-3 w-3' />
            </span>
          ) : (
            <MemberAvatar
              person={comment.user}
              size='sm'
            />
          )}
          <span className='text-sm font-medium'>
            {comment.metadata?.suggestion ? 'AI' : getCommentAuthorLabel(comment)}
          </span>
          <span className='text-muted-foreground text-xs'>
            {formatTime(new Date(comment.createdAt))}
          </span>
          {getSourceBadge()}
          {customBadges.map((badge, index) => (
            <Badge
              key={`${comment.id}-badge-${index}`}
              variant={badge.variant ?? 'outline'}
              className={badge.className}
            >
              {badge.label}
            </Badge>
          ))}
          {isResolved && (
            <Badge variant='outline' className='text-xs'>
              {comment.commentStatus}
            </Badge>
          )}
        </div>
        <div className='flex items-center gap-1'>
          {/* Reply — right-aligned, before Resolve. */}
          {!alwaysShowReplyEditor && !isResolved && comment.source !== 'AUTOMATIC_CHECK' && !isLocked && (
            <Button
              variant='ghost'
              size='sm'
              className='h-6 border-0 px-2 text-xs shadow-none hover:border-0'
              onClick={(e) => {
                e.stopPropagation()
                setReplyingToId(comment.id)
              }}
            >
              <Reply className='h-3 w-3' />
              Reply
            </Button>
          )}
          {/* 当提交审核时，显示锁定状态 */}
          {allowResolve && !isResolved && comment.source !== 'AUTOMATIC_CHECK' && !isLocked && (
            <Button
              variant='ghost'
              size='sm'
              className='h-6 border-0 px-2 text-xs shadow-none hover:border-0'
              onClick={(e) => {
                e.stopPropagation()
                onResolve(comment.id, 'RESOLVED')
              }}
            >
              <Check className='h-3 w-3' />
              Resolve
            </Button>
          )}
          {hasThreadActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='size-6'
                >
                  <MoreHorizontal className='h-3 w-3' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {allowEdit &&
                  comment.source !== 'AUTOMATIC_CHECK' &&
                  !isResolved && !isLocked &&
                  onEditComment && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsEditing(true)
                      }}
                    >
                      <SquarePen className='size-4' /> Edit
                    </DropdownMenuItem>
                  )}
                {allowCopyLink && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      copyPermalink(comment.id)
                    }}
                  >
                    <Link className=' size-4' /> Copy link
                  </DropdownMenuItem>
                )}
                {allowDelete && !isResolved && !isLocked && (
                  <DropdownMenuItem
                    variant='destructive'
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(comment.id)
                    }}
                  >
                    <Trash className=' size-4' /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Content */}
      <div className='px-3 py-3'>
        {comment.scope === 'RANGE' && comment.textRange?.text && (
          <div
            className='text-muted-foreground mb-2 w-full pl-3 text-[13px] leading-[18px]'
            style={{
              borderLeft: `2px solid ${comment.metadata?.suggestion ? 'rgba(147, 51, 234, 0.5)' : 'rgba(251, 191, 36, 0.5)'}`,
            }}
          >
            <Tooltip>
              <TooltipTrigger className='w-fit max-w-full truncate text-left'>
                {comment.textRange.text}
              </TooltipTrigger>
              <TooltipContent className='max-w-[400px]'>
                {comment.textRange.text}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {isEditing ? (
          <div
            className='space-y-2'
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <CommentEditor
              members={members}
              autoFocus={true}
              placeholder='Edit comment...'
              value={comment.content || comment.text || ''}
              jsonValue={(comment as any)?.metadata?.content}
              maxHeightClass='max-h-32'
              onSubmit={async ({ text, json }) => {
                if (text.trim() && onEditComment) {
                  await onEditComment(comment.id, text.trim(), json)
                  // Close edit mode after update completes
                  setIsEditing(false)
                }
              }}
              onCancel={() => setIsEditing(false)}
            />
          </div>
        ) : (
          <div className='text-sm'>
            <CommentViewer
              json={(comment as any)?.metadata?.content}
              text={comment.content || comment.text || ''}
            />
            {/* {!!comment.mentions?.length && (
              <div className='mt-1.5 flex flex-wrap gap-1.5'>
                {comment.mentions?.map((m, idx) => (
                  <span
                    key={`${comment.id}-m-${m.mentionedUser?.id || idx}`}
                    className='bg-primary/10 text-primary inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs'
                  >
                    @{' '}
                    {m.mentionedUser?.name || m.mentionedUser?.email || 'user'}
                  </span>
                ))}
              </div>
            )} */}
          </div>
        )}

        {/* Replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className='border-muted mt-3 space-y-2 pl-6'>
            {comment.replies.map((reply) => {
              const replySynced = isIntegrationComment(reply)
              const replyCustomBadges = (((reply as any)?.metadata?.customBadges ?? []) as Array<{
                label: string
                variant?: ComponentProps<typeof Badge>['variant']
                className?: string
              }>).filter((badge) => Boolean(badge?.label))
              const hasReplyActions =
                allowCopyLink
                || (allowEdit && !!onEditComment)
                || allowDelete

              return (
              <div key={reply.id} className='space-y-1'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    {replySynced ? (
                      <span className='bg-muted-foreground/10 text-muted-foreground flex size-5 items-center justify-center rounded-full'>
                        <Link className='h-3 w-3' />
                      </span>
                    ) : (
                      <MemberAvatar
                        person={reply.user}
                        size='sm'
                      />
                    )}
                    <span className='text-sm font-medium'>
                      {getCommentAuthorLabel(reply, 'Unknown')}
                    </span>
                    <span className='text-muted-foreground text-xs'>
                      {formatTime(new Date(reply.createdAt))}
                    </span>
                    {replyCustomBadges.map((badge, index) => (
                      <Badge
                        key={`${reply.id}-badge-${index}`}
                        variant={badge.variant ?? 'outline'}
                        className={cn('text-[10px]', badge.className)}
                      >
                        {badge.label}
                      </Badge>
                    ))}
                  </div>
                  {hasReplyActions && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant='ghost'
                          size='icon'
                          className='size-6'
                        >
                          <MoreHorizontal className='h-3 w-3' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {allowEdit && onEditComment && (
                          <DropdownMenuItem
                            onClick={() => {
                              setIsReplyEditing(true)
                              setEditingReplyId(reply.id)
                            }}
                          >
                            <SquarePen className='mr-2 size-4' /> Edit
                          </DropdownMenuItem>
                        )}
                        {allowCopyLink && (
                          <DropdownMenuItem
                            onClick={() => copyPermalink(reply.id)}
                          >
                            <Link className='mr-2 size-4' /> Copy link
                          </DropdownMenuItem>
                        )}
                        {allowDelete && (
                          <DropdownMenuItem
                            variant='destructive'
                            onClick={() => onDelete(reply.id)}
                          >
                            <Trash className='mr-2 size-4' /> Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                {isReplyEditing && reply.id === editingReplyId ? (
                  <div
                    className='space-y-2'
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <CommentEditor
                      members={members}
                      autoFocus={true}
                      placeholder='Edit reply...'
                      value={reply.content || reply.text || ''}
                      jsonValue={(reply as any)?.metadata?.content}
                      maxHeightClass='max-h-24'
                      onSubmit={async ({ text, json }) => {
                        if (text.trim() && onEditComment) {
                          await onEditComment(reply.id, text.trim(), json)
                          // Close edit mode after update completes
                          setIsReplyEditing(false)
                          setEditingReplyId(null)
                        }
                      }}
                      onCancel={() => {
                        setIsReplyEditing(false)
                        setEditingReplyId(null)
                      }}
                    />
                  </div>
                ) : (
                  <div className='text-sm'>
                    <CommentViewer
                      json={(reply as any)?.metadata?.content}
                      text={reply.content || reply.text || ''}
                    />
                    {!!reply.mentions?.length && (
                      <div className='mt-1 flex flex-wrap gap-1'>
                        {reply.mentions?.map((m, idx) => (
                          <span
                            key={`${reply.id}-m-${m.mentionedUser?.id || idx}`}
                            className='bg-primary/10 text-primary inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs'
                          >
                            @{' '}
                            {m.mentionedUser?.name ||
                              m.mentionedUser?.email ||
                              'user'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )
            })}
          </div>
        )}

        {/* Reply editor (shown when Reply button clicked) */}
        {!isResolved && comment.source !== 'AUTOMATIC_CHECK' && (replyingToId || alwaysShowReplyEditor) && (
          <div
            className='mt-4 ml-6 flex items-start gap-2'
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <MemberAvatar
              person={currentUser}
              size='sm'
              className='size-5 flex-shrink-0 rounded-full'
            />
            <div className='flex-1'>
              <CommentEditor
                members={members}
                autoFocus={true}
                placeholder='Write a reply…'
                minHeightClass={alwaysShowReplyEditor ? 'min-h-[40px]' : undefined}
                maxHeightClass={alwaysShowReplyEditor ? 'max-h-none' : 'max-h-32'}
                editorDensity={alwaysShowReplyEditor ? 'compact' : 'default'}
                editorClassName={
                  alwaysShowReplyEditor
                    ? '[&>div]:!max-h-none'
                    : undefined
                }
                onSubmit={({ text, json, mentions }) => {
                  if (text.trim()) {
                    // Always reply to the root comment (flat reply structure)
                    onReply(comment.id, text.trim(), {
                      mentions,
                      richContent: json,
                    })
                    setReplyingToId(null)
                  }
                }}
                onCancel={alwaysShowReplyEditor ? undefined : () => setReplyingToId(null)}
              />
            </div>
          </div>
        )}

        {/* Actions for automatic checks only */}
        {!isResolved && showActions && comment.source === 'AUTOMATIC_CHECK' && (
          <div className={cn('flex items-center gap-2 mt-3', (comment.metadata?.suggestion && onApplySuggestion) || onDismissCheck ? 'border-t pt-3' : '')}>
            {comment.metadata?.suggestion && onApplySuggestion && (
              <Button
                size='sm'
                onClick={() => onApplySuggestion(comment.id)}
              >
                <Check className="h-3 w-3" />
                Apply Fix
              </Button>
            )}
            {onDismissCheck && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => onDismissCheck(comment.id)}
              >
                <X className="h-3 w-3" />
                Dismiss
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
